import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import "../styles/home.scss"; // ✅ Import custom SCSS styles
import AOS from 'aos';
// Ensure Col is imported here
import { Row, Col, Navbar, Nav, Button, Accordion, Card, Form } from "react-bootstrap";
import HomeDash from '../images/home_dash_graphic.jpg'; 
import SeamlessIcon from '../images/seamless_tracking.png'; 
import NMSIcon from '../images/nominee_mgmnt.png'; 
import ADSIcon from '../images/advanced_ds.png'; 
// import TrustedVault from '../images/your-trusted_vault.png'; // Not used in your provided code
import featuremiddle from '../images/featu_middle.jpg';
import stepIconOne from '../images/create_yr_acc.png';
import stepIconTwo from '../images/add_yr_assets.png';
import stepIconThree from '../images/assign_nominies.png';
import stepIconFour from '../images/mng_monitor.png';
import planOneBg from '../images/plan_1bg.png';
import planTwoBg from '../images/plan_2bg.png';
import planThreeBg from '../images/plan_3bg.png';
import WebGraphic from '../images/web_graphic.png';
import facebook from '../images/facebook-brands-solid.png';
import instagram from '../images/square-instagram-brands-solid.png';
import twitterX from '../images/square-x-twitter-brands-solid.png';
import youTube from '../images/youtube-brands-solid.png';
import t1 from '../images/t1.png';
import t2 from '../images/t2.png';
import c1 from '../images/circ1.png';
import c2 from '../images/circ2.png';
import c3 from '../images/circ3.png';
import c4 from '../images/circ4.png';
import { Briefcase, Edit3, Users } from 'lucide-react'; // Choose appropriate icons
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Import for the new section's graph image - ensure you have this image
import NotesGraphImage from '../images/mng_monitor.jpg'; 

gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
   useEffect(() => {
    document.body.classList.remove('dark-theme');
  }, []);

   useEffect(() => {
      AOS.init({
        duration: 1000, 
        once: false, 
        mirror: false, 
      });
    }, []); 

   useEffect(() => {
      const targets = ".img-loop";
      const tween = gsap.to(targets, {
        x: 20,
        y: 20,
        duration: 3,
        ease: "power1.inOut",
        yoyo: true,
        stagger:0.8,
        repeat: -1,
      });
      return () => {
        gsap.killTweensOf(targets);
      };
    }, []);

    const imageRef = useRef(null);
    
    useEffect(() => {
      const img = imageRef.current;
      if (img) {
          gsap.set(img, {
              transformPerspective: 1000,
              transformOrigin: "center bottom",
              rotationX: 25,
              rotationZ: 0,
              y: 0,
              opacity: 1,
              scale: 1
          });
          const interestingTiltAnimation = gsap.to(img, {
              rotationX: 0,
              rotationZ: 0,
              y: 0,
              ease: "power2.out",
              scrollTrigger: {
                  trigger: img,
                  start: "top bottom",
                  end: "center center",
                  scrub: 0.6,
              }
          });
          return () => {
              if (interestingTiltAnimation.scrollTrigger) {
                  interestingTiltAnimation.scrollTrigger.kill();
              }
              gsap.killTweensOf(img);
          };
      }
  }, []);

  let FollowBox = "#Wrap .FollowBox"; // Consider moving this inside a useEffect if #Wrap might not exist on initial render
  useEffect(() => {
    gsap.set(FollowBox, {
      xPercent: -50,
      yPercent: -50,
      scale: 0
    });
  
    const handleMouseMove = (e) => {
      let mapper = gsap.utils.mapRange(0, 30, 0, 1); 
      let speed = Math.abs(e.movementX) + Math.abs(e.movementY)
      let mappedSpeed = mapper(speed);
      let clamp = gsap.utils.clamp(0, 1)
      
      gsap.to(FollowBox, {
        duration: 0.5,
        overwrite: "auto",
        x: e.clientX,
        y: e.clientY,
        stagger: 0.1,
        ease: "none"
      });
      
       gsap.to(FollowBox, {
        ease: 'none',
        duration: 0.3,
        overwrite: "auto",
        stagger: 0.1,
        scale: clamp(mappedSpeed),
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      gsap.killTweensOf(FollowBox);
    }
  }, [FollowBox]); // Added FollowBox to dependency array
  
  const layersRef = useRef([]);
    // const [showPassword, setShowPassword] = useState(false); // Not used in provided code
  
    useEffect(() => {
      const getRandom = (min, max) => Math.random() * (max - min) + min;
      const animateLayer = (layer, duration) => {
          gsap.fromTo(
              layer,
              {
                  x: getRandom(-400, 400),
                  y: getRandom(-400, 400),
                  scale: getRandom(1.1, 1.6),
                  opacity: getRandom(0.4, 0.7),
                  rotation: getRandom(-20, 20),
              },
              {
                  x: getRandom(-400, 400),
                  y: getRandom(-400, 400),
                  scale: getRandom(1.3, 1.8),
                  opacity: getRandom(0.6, 0.9),
                  rotation: getRandom(-20, 20),
                  duration,
                  repeat: -1,
                  yoyo: true,
                  ease: "power1.inOut",
              }
          );
      };
  
      layersRef.current.forEach((layer, index) => {
          if (layer) { // Check if layer exists
            const colors = [
                `radial-gradient(circle, rgba(255, 50, 150, 0.4), transparent 70%)`,
                `radial-gradient(circle, rgba(50, 150, 255, 0.4), transparent 70%)`,
                `radial-gradient(circle, rgba(150, 50, 255, 0.4), transparent 70%)`,
            ];
            layer.style.background = colors[index % colors.length];
            animateLayer(layer, getRandom(4, 6));
          }
      });
      // Cleanup for gradient layers
      return () => {
        layersRef.current.forEach(layer => {
          if (layer) gsap.killTweensOf(layer);
        });
      }
  }, []);

  // Define features for the new "Notes & Collaboration" section
  const notesSectionFeatures = [ // Renamed to avoid conflict if 'features' is used elsewhere
    {
      icon: <span className="icon-placeholder"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-briefcase">
  <rect width="20" height="14" x="2" y="7" rx="2" ry="2"/>
  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
</svg>
</span>, // Replace with actual icon
      title: 'Categorize contacts',
      description: "Monitor your team's productivity by viewing completed tasks and active projects.",
      aosDelay: "0",
    },
    {
      icon: <span className="icon-placeholder"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-edit-3">
  <path d="M12 20h9"/>
  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
</svg></span>, // Replace with actual icon
      title: 'Organize meeting notes',
      description: 'Use detailed task views, checklists, and due dates to stay on top of your projects.',
      aosDelay: "200",
    },
    {
      icon: <span className="icon-placeholder"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users">
  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
  <circle cx="9" cy="7" r="4"/>
  <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
</svg>
</span>, // Replace with actual icon
      title: 'Collaborate in real time',
      description: 'Assign tasks, share notes, and engage in real-time discussions with your team.',
      aosDelay: "400",
    },
  ];

return (
<>
<div className="homepage">
<div id="Wrap">
    <img className="FollowBox" src={c1} alt=""/> {/* Changed class to className */}
    <img className="FollowBox" src={c2} alt=""/> {/* Changed class to className */}
    <img className="FollowBox" src={c3} alt=""/> {/* Changed class to className */}
    <img className="FollowBox" src={c4} alt=""/> {/* Changed class to className */}
  </div>

{/* Navbar */}
<Navbar expand="lg" className="custom_navbar">
   <div className="container">
      <Navbar.Brand href="#" className="logo">CloudSecure</Navbar.Brand>
      <Navbar.Toggle aria-controls="navbar-nav" />
      <Navbar.Collapse id="navbar-nav">
         <Nav className="ms-auto">
            <Nav.Link href="#">Home</Nav.Link>
            <Nav.Link href="#">Services</Nav.Link>
            <Nav.Link href="#">About Us</Nav.Link>
            <Nav.Link href="#">How it Works?</Nav.Link>
            <Nav.Link href="#">Contact Us</Nav.Link>
            <Button variant="outline-light" className="btn-white">Login</Button>
            <Button variant="primary" className="signup-btn">Sign Up</Button>
         </Nav>
      </Navbar.Collapse>
   </div>
</Navbar>
{/* Hero Section */}
<section className="hero">
<div className='background'>
  <div className='circle'>
  <div className='circle1'>
    <div className='circle2'>
      <div className='circle3'>
      </div>
    </div>
  </div>
  </div>
  </div>
   <div className="container text-center">
      <h1 className="font-45">Your Trusted Solution<br /> for Secure Asset<br /> Management</h1>
      <p className="my-4">Securely store and manage your credentials and assets.<br /> 
         Share access with trusted nominees anytime
      </p>
      <Button variant="dark" className="cta-btn px-4 py-2">Get Started Now</Button>
   </div>
</section>

{/* DashBoard Preview */}
<section className="home_dash">
   <div className="container text-center">
      <div className="col-md-12 px-0 mx-auto">
         <img src={HomeDash} alt="CloudSecure Dashboard" ref={imageRef} className="homeDash" width="100%"/>
      </div>
   </div>
</section>
{/* Features */}
<section className="features ">
   <div className="container">
      <div className="row text-center">
         <div className="col-md-4" data-aos="fade-up" >
            <img src={SeamlessIcon} alt="Seamless Asset Tracking Icon" className="mb-4" width="72"/>
            <h4>Seamless Asset<br /> Tracking</h4>
         </div>
         <div className="col-md-4" data-aos="fade-up" data-aos-delay="400">
            <img src={NMSIcon} alt="Nominee Management System Icon" className="mb-4" width="72"/>
            <h4>Nominee Management<br />  System</h4>
         </div>
         <div className="col-md-4" data-aos="fade-up" data-aos-delay="800">
            <img src={ADSIcon} alt="Advanced Data Security Icon" className="mb-4" width="72"/>
            <h4>Advanced Data<br />  Security</h4>
         </div>
      </div>
   </div>
</section>
{/* Asset Vault Section */}
<section className="asset-vault">
   <div className="container">
      <div className="">
         <Row className="align-items-center">
            <div className="col-md-6 text-align-center" data-aos="fade-right">
               <div className="trust-images" data-aos="fade-up"> {/* Changed class to className */}
               <img src={t1} alt="Trusted vault illustration 1" className="mb-4 t1 img-loop" />
               <img src={t2} alt="Trusted vault illustration 2" className="mb-4 t2 img-loop" />
               </div>
            </div>
            <div className="col-md-6" data-aos="fade-left">
               <h2 >Your Trusted Asset Vault</h2>
               <p className="mt-3">We offer a secure, modern solution to store, organize, and protect your assets, giving you full control with simplicity and ease.</p>
            </div>
         </Row>
      </div>
   </div>
</section>

{/* How It Works Section */}
<section className="how_it_works">
   <div className="container">
      <div className="how_it_works_iinr ">
         <Row className="align-items-cente"> {/* Typo: align-items-center? */}
            <div className="col-md-3" >
               <ul className="how_steps">
                  <li data-aos="fade-up">
                     <img src={stepIconOne} alt="Create account icon" className="mb-4" width="74"/>
                     Create Your<br /> 
                     Account
                  </li>
                  <li data-aos="fade-up" data-aos-delay="200">
                     <img src={stepIconTwo} alt="Add assets icon" className="mb-4" width="74"/>
                     Add Your<br /> 
                     Assets
                  </li>
               </ul>
            </div>
            <div className="col-md-6" data-aos="zoom-out-up">
               <div className="animated-liquid">
                  <img src={featuremiddle} alt="Central feature illustration" width="100%"/>
               </div>
            </div>
            <div className="col-md-3" data-aos="fade-up">
               <ul className="how_steps">
                  <li data-aos="fade-up" data-aos-delay="400">
                     <img src={stepIconThree} alt="Assign nominees icon" className="mb-4" width="74"/>
                     Assign<br/> {/* Changed from Create Your Account */}
                     Nominees
                  </li>
                  <li data-aos="fade-up" data-aos-delay="600">
                     <img src={stepIconFour} alt="Manage and monitor icon" className="mb-4" width="74"/>
                     Manage &<br /> {/* Changed from Add Your Assets */}
                     Monitor
                  </li>
               </ul>
            </div>
         </Row>
      </div>
   </div>
</section>

{/* Pricing */}
<section className="pricing ">
   <div className="container">
      <div className="text-center">
         <h2>Pricing</h2>
         <p>Choose the Plan That Suits You Best</p>
      </div>
      <Row className="align-items-center">
         <div className="col-md-4" data-aos="flip-left" >
            <div className="plan_container plan_one">
               <img src={planOneBg} alt="Basic plan background" className="plan_bg" />
               <h2>
                  <span>Basic</span>
                  Free
               </h2>
               <p>Ideal for personal use with 
                  essential features and up to 5 assets.
               </p>
               <ul>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="price_check_basic_1" data-name="price_check" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_basic_1" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)"/>
                           <path id="Path_3_basic_1" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Store up to 5 assets.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="price_check_basic_2" data-name="price_check" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_basic_2" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)"/>
                           <path id="Path_3_basic_2" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Secure encryption for your credentials.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                       <g id="price_check_basic_3" data-name="price_check" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_basic_3" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)"/>
                           <path id="Path_3_basic_3" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Basic nominee assignment.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="price_check_basic_4" data-name="price_check" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_basic_4" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)"/>
                           <path id="Path_3_basic_4" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Limited support access.
                  </li>
               </ul>
            </div>
         </div>
         <div className="col-md-4" data-aos="flip-left" data-aos-delay="200">
            <div className="plan_container plan_two">
               <img src={planTwoBg} alt="Pro plan background" className="plan_bg" />
               <h2>
                  <span>Pro</span>
                  $9.99/month
               </h2>
               <p>Unlimited assets, nominee 
                  management, and advanced analytics.
               </p>
               <ul>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_pro_1" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_pro_1" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_pro_1" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Unlimited assets and storage.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_pro_2" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_pro_2" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_pro_2" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Advanced nominee management.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_pro_3" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_pro_3" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_pro_3" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Real-time updates and access logs.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_pro_4" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_pro_4" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_pro_4" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Priority customer support.
                  </li>
               </ul>
            </div>
         </div>
         <div className="col-md-4" data-aos="flip-left" data-aos-delay="400">
            <div className="plan_container plan_three">
               <img src={planThreeBg} alt="Enterprise plan background" className="plan_bg" />
               <h2>
                  <span>Enterprise</span>
                  $9.99/month
               </h2>
               <p>Tailored for businesses with security 
                  and multiple support.
               </p>
               <ul>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_ent_1" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_ent_1" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_ent_1" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Tailored solutions for businesses.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_ent_2" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_ent_2" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_ent_2" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Multi-user accounts with admin controls.
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_ent_3" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_ent_3" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_ent_3" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     Comprehensive analytics
                  </li>
                  <li>
                     <svg className="me-3" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                        <g id="Group_33_ent_4" data-name="Group 33" transform="translate(-133 -982)">
                           <circle id="Ellipse_1_ent_4" data-name="Ellipse 1" cx="9" cy="9" r="9" transform="translate(133 982)" fill="#3456f0"/>
                           <path id="Path_3_ent_4" data-name="Path 3" d="M1633.544,1211.423l3.008,3.008,5.328-5.328" transform="translate(-1496.044 -220.603)" fill="none" stroke="#fff" strokeLinecap="round" strokeWidth="2"/>
                        </g>
                     </svg>
                     24/7 premium support.
                  </li>
               </ul>
            </div>
         </div>
      </Row>
   </div>
</section>

{/* Web Graphic Separator */}
<div>
   <img src={WebGraphic} alt="Decorative web graphic separator" width="100%"/>
</div>

{/* Notes & Collaboration Section - ADDED HERE AS AN EXAMPLE */}
<section className="notes-collaboration-section">
  <div className="container">
  
    <h2 className="section-title text-center">
      Capture meeting notes <br /> & collaborate with ease
    </h2>
    <p className="section-subtitle text-center">
      Organize all of your notes and contact in one central place
    </p>
    <Row className="align-items-center mt-5">
      <Col md={6} className="notes-features-col">
        {notesSectionFeatures.map((feature, index) => ( // Using notesSectionFeatures
          <div key={index} className="feature-item d-flex align-items-start mb-4" data-aos="fade-right" data-aos-delay={feature.aosDelay}>
            <div className="feature-icon-wrapper me-3">
              {feature.icon}
            </div>
            <div>
              <h5 className="feature-title">{feature.title}</h5>
              <p className="feature-description">{feature.description}</p>
            </div>
          </div>
        ))}
      </Col>
      <Col md={6} className="notes-graph-col text-center" data-aos="fade-left" data-aos-delay="300">
        <img src={NotesGraphImage} alt="Notes and collaboration statistics" className="img-fluid notes-graph-image" />
      </Col>
    </Row>
  </div>
</section>


{/* FAQ Section */}
<section className="faq ">
   <div className="container">
      <div className="col-md-8 mx-auto px-0">
        <div className="faq_list">
         <h2 className="text-center pb-5">Frequently Asked Questions</h2>
         <Accordion>
            <Accordion.Item eventKey="0" className="mb-3">
               <Accordion.Header>What is Cloudsecure ?</Accordion.Header>
               <Accordion.Body>CloudSecure is a comprehensive digital vault designed to help you securely store, manage, and organize your important assets and credentials, with robust features for nominee assignment and data protection.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1" className="mb-3">
               <Accordion.Header>How secure is my data?</Accordion.Header>
               <Accordion.Body>We use advanced encryption protocols and multi-layered security measures to ensure your data remains private, protected, and accessible only by you and your designated nominees.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2" className="mb-3">
               <Accordion.Header>How can I assign nominees to access my assets?</Accordion.Header>
               <Accordion.Body>Our intuitive nominee management system allows you to easily assign trusted individuals to specific assets with customizable access permissions and conditions, ensuring a secure and controlled transfer when needed.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3" className="mb-3">
               <Accordion.Header>What happens if I lose my login details?</Accordion.Header>
               <Accordion.Body>CloudSecure offers secure account recovery options. We recommend setting up multi-factor authentication and recovery methods during sign-up to ensure you can regain access safely.</Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4" className="mb-3">
               <Accordion.Header> Is there a free plan available?</Accordion.Header>
               <Accordion.Body>Yes, we offer a Basic free plan that includes essential features for managing a limited number of assets. For more advanced capabilities and unlimited assets, you can explore our Pro and Enterprise plans.</Accordion.Body>
            </Accordion.Item>
         </Accordion>
      </div>
      </div>
   </div>
</section>
{/* Contact Form */}
<section className="contact ">
   <div className="container">
      <div className="hm-ct_container">
          {/* Animated Background */}
      <div className="gradient-container">
        {[...Array(3)].map((_, i) => (
          <div 
            key={i} 
            className="gradient-layer" 
            ref={(el) => { // Ensure el is not null before pushing
                if (el && !layersRef.current.includes(el)) {
                     layersRef.current.push(el);
                }
            }}
          ></div>
        ))}
      </div>
         <div className="col-md-7 px-0 mx-auto">
            <h2 className="color-white mb-3">The CloudSecure is only a few clicks away</h2>
            <p className="color-white mb-5">Contact us now</p>
            <Form>
               <Form.Group controlId="contactFullName" className="form-floating mb-3">
                  <Form.Control type="text" placeholder="Full Name" />
                  <Form.Label >Name</Form.Label>
               </Form.Group>
               <Form.Group controlId="contactEmail" className="form-floating mb-3">
                  <Form.Control type="email" placeholder="Email Address" />
                  <Form.Label>Email address</Form.Label>
               </Form.Group>
               <Form.Group controlId="contactPhone" className="form-floating mb-3">
                  <Form.Control type="text" placeholder="Phone Number" />
                  <Form.Label>Phone</Form.Label>
               </Form.Group>
               <Form.Group controlId="contactQuestion" className="form-floating mb-3">
                  <Form.Control as="textarea" placeholder="Your Question" style={{height: '100px'}} />
                  <Form.Label>Question</Form.Label>
               </Form.Group>
               <div className="col-md-3 mt-5">
                  <Button variant="primary" className="btn-block">Submit</Button>
               </div>
            </Form>
         </div>
      </div>
   </div>
</section>
<div className="newsletter-section py-5 mt-5"> {/* Changed class to className */}
   <div className="container">
      <div className="col-md-7 px-0 mx-auto">
         <h2 className="color-white text-center">Subscribe to our Newsletter</h2>
         <Form className="mt-5">
            <input type="email" className="form-control" placeholder="Enter your email address" required />
            <button type="submit">Subscribe</button>
         </Form>
      </div>
   </div>
</div>
<footer>
   <div className="footer-content py-5"> {/* Changed class to className */}
      <div className="container">
         <h3 className="brand text-center">CloudSecure</h3> {/* Changed class to className */}
         <div className="footer_middle">
            <Row className="align-items-center">
               <div className="col-md-6">
                  <div className="social-icons"> {/* Changed class to className */}
                     <a href="#"><img src={facebook} alt="Facebook"/></a>
                     <a href="#"><img src={instagram} alt="Instagram"/></a>
                     <a href="#"><img src={twitterX} alt="X (Twitter)"/></a>
                     <a href="#"><img src={youTube} alt="YouTube"/></a>
                  </div>
               </div>
               <div className="col-md-6">
                  <div className="footer-links"> {/* Changed class to className */}
                     <a href="#">Contact Us</a>
                     <a href="#">Privacy Policy</a>
                     <a href="#">Terms & Conditions</a>
                  </div>
               </div>
            </Row>
         </div>
         <p className="copyright text-center">&copy; All Rights Reserved CloudSecure 2025.</p> {/* Changed class to className */}
      </div>
   </div>
</footer>
</div>
</>
);
};
export default HomePage;
